/**
 * Выдвижной список (agroai-6al.61): «Беседы» и «Отчёты» внутри панели, поверх
 * беседы (~360 px, ширина тянется). Закрывается кликом вне и Esc. У строки — меню «⋯».
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { FileChartColumn, Link2, Plus, Search } from 'lucide-react';
import { Icon } from '../astra/ui/Icon';
import { InlineTitle, KebabMenu } from './KebabMenu';
import { chatTitle, type ChatSummary } from './history';
import {
  deleteSavedReport,
  listSavedReports,
  publicUrl,
  renameSavedReport,
  shareSavedReport,
  unshareSavedReport,
  type SavedReportEntry,
} from './report/savedReports';
import { defineMessages } from '../i18n/define';
import { useT } from '../i18n/hooks';

const m = defineMessages({
  groupPinned: { id: 'ai.drawer.group.pinned', defaultMessage: 'Закреплённые', description: 'Заголовок группы в списке бесед ИИ: закреплённые беседы' },
  groupToday: { id: 'ai.drawer.group.today', defaultMessage: 'Сегодня', description: 'Заголовок группы в списке бесед ИИ: беседы за сегодня' },
  groupYesterday: { id: 'ai.drawer.group.yesterday', defaultMessage: 'Вчера', description: 'Заголовок группы в списке бесед ИИ: беседы за вчера' },
  groupWeek: { id: 'ai.drawer.group.week', defaultMessage: 'За 7 дней', description: 'Заголовок группы в списке бесед ИИ: беседы за последнюю неделю' },
  groupOlder: { id: 'ai.drawer.group.older', defaultMessage: 'Раньше', description: 'Заголовок группы в списке бесед ИИ: более старые беседы' },
  close: { id: 'ai.drawer.close', defaultMessage: 'Закрыть список', description: 'Подпись для диктора затемнения за списком бесед: клик закрывает список' },
  navAria: { id: 'ai.drawer.navAria', defaultMessage: 'Беседы и отчёты', description: 'Подпись для диктора выдвижного списка бесед и отчётов панели ИИ' },
  widthAria: { id: 'ai.drawer.widthAria', defaultMessage: 'Ширина списка', description: 'Подпись для диктора ручки изменения ширины списка бесед' },
  tabsAria: { id: 'ai.drawer.tabsAria', defaultMessage: 'Раздел списка', description: 'Подпись для диктора вкладок «Беседы» и «Отчёты»' },
  tabChats: { id: 'ai.drawer.tab.chats', defaultMessage: 'Беседы', description: 'Вкладка выдвижного списка панели ИИ; коротко' },
  tabReports: { id: 'ai.drawer.tab.reports', defaultMessage: 'Отчёты', description: 'Вкладка выдвижного списка панели ИИ: сохранённые отчёты; коротко' },
  newChat: { id: 'ai.drawer.newChat', defaultMessage: 'Новая беседа', description: 'Кнопка в списке бесед: начать новую беседу с ИИ' },
  searchPlaceholder: { id: 'ai.drawer.searchPlaceholder', defaultMessage: 'Поиск по названию', description: 'Подсказка в поле поиска по спискам бесед и отчётов' },
  searchChats: { id: 'ai.drawer.searchChats', defaultMessage: 'Поиск по названию беседы', description: 'Подпись для диктора поля поиска на вкладке «Беседы»' },
  searchReports: { id: 'ai.drawer.searchReports', defaultMessage: 'Поиск по названию отчёта', description: 'Подпись для диктора поля поиска на вкладке «Отчёты»' },
  historyFailed: { id: 'ai.drawer.historyFailed', defaultMessage: 'История не загрузилась: {error}', description: 'Ошибка над списком бесед; {error} — причина' },
  loading: { id: 'ai.drawer.loading', defaultMessage: 'Загружаю…', description: 'Состояние загрузки списков бесед и отчётов (от первого лица)' },
  notFound: { id: 'ai.drawer.notFound', defaultMessage: 'Ничего не найдено.', description: 'Пустой результат поиска по беседам или отчётам' },
  noChats: { id: 'ai.drawer.noChats', defaultMessage: 'Прошлых бесед пока нет.', description: 'Пустое состояние списка бесед' },
  chatTitleLabel: { id: 'ai.drawer.chatTitleLabel', defaultMessage: 'Название беседы', description: 'Подпись для диктора поля переименования беседы' },
  rowActions: { id: 'ai.drawer.rowActions', defaultMessage: 'Действия: {title}', description: 'Подпись для диктора меню «⋯» у строки беседы или отчёта; {title} — название' },
  rename: { id: 'ai.drawer.rename', defaultMessage: 'Переименовать', description: 'Пункт меню строки: переименовать' },
  pin: { id: 'ai.drawer.pin', defaultMessage: 'Закрепить', description: 'Пункт меню строки беседы: закрепить' },
  unpin: { id: 'ai.drawer.unpin', defaultMessage: 'Открепить', description: 'Пункт меню строки беседы: открепить' },
  delete: { id: 'ai.drawer.delete', defaultMessage: 'Удалить', description: 'Пункт меню строки: удалить (опасное действие)' },
  confirmDelete: { id: 'ai.drawer.confirmDelete', defaultMessage: 'Подтвердить удаление', description: 'Второй шаг пункта «Удалить»: подтверждение' },
  ttlNote: { id: 'ai.drawer.ttlNote', defaultMessage: 'Незакреплённые беседы удаляются через {days, plural, one {# день} few {# дня} many {# дней} other {# дня}}. Закреплённые хранятся всегда.', description: 'Примечание внизу списка бесед; # — через сколько дней удаляются незакреплённые' },
  linkCopied: { id: 'ai.drawer.linkCopied', defaultMessage: 'Ссылка скопирована. Любой, у кого она есть, увидит отчёт без входа.', description: 'Уведомление после копирования публичной ссылки на отчёт: предупреждение, что ссылка открывает отчёт без входа' },
  copyManually: { id: 'ai.drawer.copyManually', defaultMessage: 'Скопируйте вручную: {url}', description: 'Уведомление, когда ссылку не удалось скопировать автоматически; {url} — адрес' },
  reportsFailed: { id: 'ai.drawer.reportsFailed', defaultMessage: 'Список отчётов не загрузился: {error}', description: 'Ошибка над списком отчётов; {error} — причина' },
  noReports: { id: 'ai.drawer.noReports', defaultMessage: 'Сохранённых отчётов пока нет. Попросите ИИ собрать отчёт и нажмите «Сохранить».', description: 'Пустое состояние списка отчётов; «Сохранить» — название кнопки в отчёте' },
  reportTitleLabel: { id: 'ai.drawer.reportTitleLabel', defaultMessage: 'Название отчёта', description: 'Подпись для диктора поля переименования отчёта' },
  publicLink: { id: 'ai.drawer.publicLink', defaultMessage: 'Есть публичная ссылка', description: 'Подсказка значка у отчёта, на который выдана публичная ссылка' },
  copyLink: { id: 'ai.drawer.copyLink', defaultMessage: 'Скопировать ссылку', description: 'Пункт меню отчёта: скопировать публичную ссылку' },
  revokeLink: { id: 'ai.drawer.revokeLink', defaultMessage: 'Отозвать ссылку', description: 'Пункт меню отчёта: отключить публичную ссылку' },
});

export type DrawerTab = 'chats' | 'reports';

const GROUPS = ['pinned', 'today', 'yesterday', 'week', 'older'] as const;
const GROUP_LABEL = { pinned: m.groupPinned, today: m.groupToday, yesterday: m.groupYesterday, week: m.groupWeek, older: m.groupOlder } as const;

/** Группа беседы в списке: закреплённые отдельно, остальные — по давности. */
export function chatGroup(chat: ChatSummary, now = new Date()): (typeof GROUPS)[number] {
  if (chat.pinned) return 'pinned';
  const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diff = Math.round((day(now) - day(new Date(chat.updated_at))) / 86_400_000);
  if (diff <= 0) return 'today';
  if (diff === 1) return 'yesterday';
  if (diff <= 7) return 'week';
  return 'older';
}

export interface ChatDrawerProps {
  tab: DrawerTab;
  onTab: (tab: DrawerTab) => void;
  onClose: () => void;
  chats: ChatSummary[] | null;
  chatId: string | null;
  error: string | null;
  ttlDays: number;
  onNew: () => void;
  onOpenChat: (id: string) => void;
  onRenameChat: (id: string, title: string) => void;
  onPinChat: (id: string, pinned: boolean) => void;
  onDeleteChat: (id: string) => void;
  onOpenReport: (id: string) => void;
  user: string;
  /** Отчёт переименован/удалён — панель обновляет открытый холст. */
  onReportChanged?: (id: string, change: { title?: string; deleted?: boolean }) => void;
}

const WIDTH_KEY = 'agroai-agent-drawer-width-v1';
const DRAWER_DEFAULT = 360;
const DRAWER_MIN = 240;

function readDrawerWidth(): number {
  try {
    const value = Number(window.localStorage.getItem(WIDTH_KEY));
    return Number.isFinite(value) && value >= DRAWER_MIN ? value : DRAWER_DEFAULT;
  } catch {
    return DRAWER_DEFAULT;
  }
}

export function ChatDrawer(props: ChatDrawerProps) {
  const t = useT();
  const { tab } = props;
  const [width, setWidth] = useState(readDrawerWidth);
  const drag = useRef<{ x: number; width: number } | null>(null);
  const move = (next: number) => {
    const cap = Math.floor((box.current?.parentElement?.clientWidth || 10_000) * 0.85);
    const value = Math.max(DRAWER_MIN, Math.min(cap, Math.round(next)));
    setWidth(value);
    try {
      window.localStorage.setItem(WIDTH_KEY, String(value));
    } catch {
      /* без хранилища ширина живёт до закрытия */
    }
  };
  const [query, setQuery] = useState('');
  const box = useRef<HTMLElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      props.onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [props]);

  return (
    <div className="agent-drawer-layer">
      <button type="button" className="agent-drawer-scrim" aria-label={t(m.close)} tabIndex={-1} onClick={props.onClose} />
      <nav className="agent-drawer" ref={box} aria-label={t(m.navAria)} style={{ '--agent-drawer-width': `${width}px` } as CSSProperties}>
        <div
          className="agent-drawer__resize"
          role="separator"
          aria-orientation="vertical"
          aria-label={t(m.widthAria)}
          aria-valuemin={DRAWER_MIN}
          aria-valuenow={width}
          tabIndex={0}
          onPointerDown={(event) => {
            if (event.button !== 0) return;
            event.preventDefault();
            drag.current = { x: event.clientX, width: box.current?.offsetWidth || width };
            try {
              event.currentTarget.setPointerCapture?.(event.pointerId);
            } catch {
              /* без захвата */
            }
          }}
          onPointerMove={(event) => drag.current && move(drag.current.width + event.clientX - drag.current.x)}
          onPointerUp={() => (drag.current = null)}
          onPointerCancel={() => (drag.current = null)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowRight') move(width + 16);
            else if (event.key === 'ArrowLeft') move(width - 16);
            else return;
            event.preventDefault();
          }}
        />
        <div className="agent-drawer__tabs" role="tablist" aria-label={t(m.tabsAria)}>
          {(['chats', 'reports'] as const).map((id) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={`agent-drawer__tab focus-ring${tab === id ? ' agent-drawer__tab--on' : ''}`}
              onClick={() => {
                setQuery('');
                props.onTab(id);
              }}
            >
              {id === 'chats' ? t(m.tabChats) : t(m.tabReports)}
            </button>
          ))}
        </div>
        {tab === 'chats' ? (
          <button type="button" className="agent-history__new focus-ring" onClick={props.onNew}>
            <Icon icon={Plus} size={16} />
            <span>{t(m.newChat)}</span>
          </button>
        ) : null}
        <label className="agent-saved__search">
          <Icon icon={Search} size={16} />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t(m.searchPlaceholder)}
            aria-label={tab === 'chats' ? t(m.searchChats) : t(m.searchReports)}
          />
        </label>
        {tab === 'chats' ? <ChatsTab {...props} query={query} /> : <ReportsTab {...props} query={query} />}
      </nav>
    </div>
  );
}

function ChatsTab({ chats, chatId, error, ttlDays, query, onOpenChat, onRenameChat, onPinChat, onDeleteChat }: ChatDrawerProps & { query: string }) {
  const t = useT();
  const [renaming, setRenaming] = useState<string | null>(null);
  const grouped = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const shown = (chats ?? []).filter((item) => !needle || item.title.toLowerCase().includes(needle));
    return GROUPS.map((name) => ({ name, items: shown.filter((item) => chatGroup(item) === name) })).filter((g) => g.items.length > 0);
  }, [chats, query]);
  return (
    <div className="agent-drawer__body">
      {error ? <p className="agent-error">{t(m.historyFailed, { error })}</p> : null}
      {chats === null && !error ? <p className="agent-history__note">{t(m.loading)}</p> : null}
      {chats !== null && grouped.length === 0 ? (
        <p className="agent-history__note">{query ? t(m.notFound) : t(m.noChats)}</p>
      ) : null}
      {grouped.map((group) => (
        <section key={group.name} aria-label={t(GROUP_LABEL[group.name])}>
          <h3 className="agent-drawer__group">{t(GROUP_LABEL[group.name])}</h3>
          <ul className="agent-history__list">
            {group.items.map((item) => (
              <li key={item.id} className="agent-drawer__row">
                {renaming === item.id ? (
                  <InlineTitle
                    value={chatTitle(item.title)}
                    label={t(m.chatTitleLabel)}
                    onSave={(next) => {
                      setRenaming(null);
                      onRenameChat(item.id, next);
                    }}
                    onCancel={() => setRenaming(null)}
                  />
                ) : (
                  <button
                    type="button"
                    className={`agent-history__item focus-ring${item.id === chatId ? ' agent-history__item--current' : ''}`}
                    aria-current={item.id === chatId ? 'true' : undefined}
                    onClick={() => onOpenChat(item.id)}
                  >
                    <span className="agent-history__title">{chatTitle(item.title)}</span>
                  </button>
                )}
                <KebabMenu
                  label={t(m.rowActions, { title: chatTitle(item.title) })}
                  items={[
                    { id: 'rename', label: t(m.rename), onSelect: () => setRenaming(item.id) },
                    { id: 'pin', label: item.pinned ? t(m.unpin) : t(m.pin), onSelect: () => onPinChat(item.id, !item.pinned) },
                    { id: 'delete', label: t(m.delete), confirm: t(m.confirmDelete), danger: true, onSelect: () => onDeleteChat(item.id) },
                  ]}
                />
              </li>
            ))}
          </ul>
        </section>
      ))}
      <p className="agent-history__note">{t(m.ttlNote, { days: ttlDays })}</p>
    </div>
  );
}

function ReportsTab({ query, onOpenReport, onReportChanged }: ChatDrawerProps & { query: string }) {
  const t = useT();
  const [items, setItems] = useState<SavedReportEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let live = true;
    const timer = setTimeout(
      () =>
        void listSavedReports(query).then(
          (list) => live && (setItems(list), setError(null)),
          (reason: unknown) => live && setError(reason instanceof Error ? reason.message : String(reason)),
        ),
      query ? 250 : 0,
    );
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [query, tick]);

  const fail = (reason: unknown) => setError(reason instanceof Error ? reason.message : String(reason));
  const copyLink = (item: SavedReportEntry) =>
    shareSavedReport(item.id).then(
      (next) =>
        navigator.clipboard.writeText(publicUrl(next.share_token ?? '')).then(
          () => {
            setNote(t(m.linkCopied));
            setTick((n) => n + 1);
          },
          () => setNote(t(m.copyManually, { url: publicUrl(next.share_token ?? '') })),
        ),
      fail,
    );

  return (
    <div className="agent-drawer__body">
      {error ? <p className="agent-error">{t(m.reportsFailed, { error })}</p> : null}
      {note ? <p className="agent-history__note">{note}</p> : null}
      {items === null && !error ? <p className="agent-history__note">{t(m.loading)}</p> : null}
      {items?.length === 0 ? (
        <p className="agent-history__note">{query ? t(m.notFound) : t(m.noReports)}</p>
      ) : null}
      <ul className="agent-history__list">
        {items?.map((item) => (
          <li key={item.id} className="agent-drawer__row">
            {renaming === item.id ? (
              <InlineTitle
                value={item.title}
                label={t(m.reportTitleLabel)}
                onSave={(next) => {
                  setRenaming(null);
                  void renameSavedReport(item.id, next).then(() => {
                    onReportChanged?.(item.id, { title: next });
                    setTick((n) => n + 1);
                  }, fail);
                }}
                onCancel={() => setRenaming(null)}
              />
            ) : (
              <button type="button" className="agent-history__item focus-ring" onClick={() => onOpenReport(item.id)}>
                <Icon icon={FileChartColumn} size={16} />
                <span className="agent-history__title">{item.title}</span>
                {item.shared ? <Icon icon={Link2} size={16} title={t(m.publicLink)} /> : null}
              </button>
            )}
            <KebabMenu
              label={t(m.rowActions, { title: item.title })}
              items={[
                { id: 'rename', label: t(m.rename), onSelect: () => setRenaming(item.id) },
                { id: 'copy', label: t(m.copyLink), onSelect: () => void copyLink(item) },
                {
                  id: 'unshare',
                  label: t(m.revokeLink),
                  hidden: !item.shared,
                  onSelect: () => void unshareSavedReport(item.id).then(() => setTick((n) => n + 1), fail),
                },
                {
                  id: 'delete',
                  label: t(m.delete),
                  confirm: t(m.confirmDelete),
                  danger: true,
                  onSelect: () =>
                    void deleteSavedReport(item.id).then(() => {
                      onReportChanged?.(item.id, { deleted: true });
                      setTick((n) => n + 1);
                    }, fail),
                },
              ]}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}
